import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  LogOut,
  BarChart2,
  TrendingUp,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { cn, isAdminRole } from '../lib/utils';
import { useTenderStatus } from '../lib/tenderStatus';
import { RoleBadge } from './Badge';

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/clients', label: 'Kliendid', icon: Users },
  { to: '/projects', label: 'Projektid', icon: FolderKanban },
  { to: '/finance', label: 'Finantsid', icon: BarChart2 },
];

export default function Sidebar() {
  const currentUser = useStore((s) => s.currentUser);
  const logout = useStore((s) => s.logout);
  const navigate = useNavigate();
  // Müük audience only — the badge shows tenders still awaiting a decision.
  const canSeeSales = !!currentUser && ['super_admin', 'admin', 'sales', 'sales_pm'].includes(currentUser.role);
  const tenderStatus = useTenderStatus(canSeeSales);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside className="w-60 bg-gray-900 flex flex-col h-full shrink-0">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-gray-800">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-brand-500 flex items-center justify-center">
            <span className="text-white font-bold text-sm">vD</span>
          </div>
          <div>
            <div className="text-white font-semibold text-sm leading-none">vDisain PM</div>
            <div className="text-gray-400 text-xs mt-0.5">Project Manager</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-thin">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 mb-2">
          Main Menu
        </div>
        {NAV_ITEMS.map(({ to, label, icon: Icon, exact }) => (
          <div key={to}>
            <NavLink
              to={to}
              end={exact}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                  isActive
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                )
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
            {/* Disain workload board lives under Projektid */}
            {to === '/projects' && currentUser && ['super_admin', 'admin', 'sales', 'sales_pm', 'pm'].includes(currentUser.role) && (
              <NavLink
                to="/projects/design"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 pl-10 pr-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                    isActive
                      ? 'bg-brand-500 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  )
                }
              >
                Disain
              </NavLink>
            )}
          </div>
        ))}

        {currentUser && ['super_admin', 'admin', 'sales', 'sales_pm'].includes(currentUser.role) && (
          <>
            <NavLink
              to="/sales"
              end
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                  isActive
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                )
              }
            >
              <TrendingUp size={16} />
              Müük
            </NavLink>
            <NavLink
              to="/sales/inquiries"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 pl-10 pr-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                  isActive
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                )
              }
            >
              Päringud
            </NavLink>
            <NavLink
              to="/sales/tenders"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 pl-10 pr-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                  isActive
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                )
              }
            >
              Hanked
              {!!tenderStatus?.undecided && (
                <span className="ml-auto px-1.5 py-0.5 rounded-full bg-brand-500/20 text-brand-200 text-[10px] font-semibold">
                  {tenderStatus.undecided}
                </span>
              )}
            </NavLink>
            <NavLink
              to="/sales/stats"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 pl-10 pr-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                  isActive
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                )
              }
            >
              Statistika
            </NavLink>
          </>
        )}

        {isAdminRole(currentUser?.role) && (
          <>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 mt-5 mb-2">
              Administration
            </div>
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                  isActive
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                )
              }
            >
              <Users size={16} />
              User Management
            </NavLink>
          </>
        )}
      </nav>

      {/* User section */}
      <div className="px-3 py-4 border-t border-gray-800">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-800 transition-colors">
          <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
            {currentUser?.initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-medium truncate">{currentUser?.name}</div>
            <div className="mt-0.5">
              <RoleBadge role={currentUser?.role ?? ''} />
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
            title="Logout"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
}
